export type ReaderKind = "novel" | "manga" | "anime";

export interface HotkeySequence {
  id: string;
  label: string;
  kind: ReaderKind;
  defaultBindings: string[];
}

type CustomBindings = Record<string, string[]>;

export const HOTKEY_SEQUENCES: HotkeySequence[] = [
  // ── Novel ──
  {
    id: "novel:chapterPrev",
    label: "Previous Chapter",
    kind: "novel",
    defaultBindings: ["A", "ArrowLeft", "Backspace"],
  },
  {
    id: "novel:chapterNext",
    label: "Next Chapter",
    kind: "novel",
    defaultBindings: ["D", "ArrowRight", "Space"],
  },
  {
    id: "novel:scrollUp",
    label: "Page Up",
    kind: "novel",
    defaultBindings: ["W", "ArrowUp", "PageUp"],
  },
  {
    id: "novel:scrollDown",
    label: "Page Down",
    kind: "novel",
    defaultBindings: ["S", "ArrowDown", "PageDown"],
  },

  // ── Manga ──
  {
    id: "manga:chapterPrev",
    label: "Previous Chapter",
    kind: "manga",
    defaultBindings: ["A", "ArrowLeft", "Backspace"],
  },
  {
    id: "manga:chapterNext",
    label: "Next Chapter",
    kind: "manga",
    defaultBindings: ["D", "ArrowRight", "Space"],
  },
  {
    id: "manga:pagePrev",
    label: "Previous Page",
    kind: "manga",
    defaultBindings: ["W", "ArrowUp", "PageUp"],
  },
  {
    id: "manga:pageNext",
    label: "Next Page",
    kind: "manga",
    defaultBindings: ["S", "ArrowDown", "PageDown"],
  },

  // ── Anime ──
  {
    id: "anime:episodePrev",
    label: "Previous Episode",
    kind: "anime",
    defaultBindings: ["A", "ArrowLeft", "Backspace"],
  },
  {
    id: "anime:episodeNext",
    label: "Next Episode",
    kind: "anime",
    defaultBindings: ["D", "ArrowRight", "Space"],
  },
  {
    id: "anime:skipBack5s",
    label: "Skip Back 5s",
    kind: "anime",
    defaultBindings: ["J", "Shift+ArrowLeft", "PageUp"],
  },
  {
    id: "anime:skipForward5s",
    label: "Skip Forward 5s",
    kind: "anime",
    defaultBindings: ["L", "Shift+ArrowRight", "PageDown"],
  },
];

export const DEFAULT_BINDINGS: CustomBindings = Object.fromEntries(
  HOTKEY_SEQUENCES.map((s) => [s.id, s.defaultBindings]),
);

const STORAGE_KEY = "anima-hotkey-bindings";

export function loadCustomBindings(): CustomBindings {
  if (typeof window === "undefined") return { ...DEFAULT_BINDINGS };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_BINDINGS };
    const parsed = JSON.parse(raw) as Partial<CustomBindings>;
    // Merge: use custom where valid, fall back to default
    const merged: CustomBindings = {};
    for (const seq of HOTKEY_SEQUENCES) {
      const custom = parsed[seq.id];
      merged[seq.id] =
        Array.isArray(custom) && custom.length > 0
          ? custom.filter((b) => typeof b === "string" && b.length > 0).slice(0, 3)
          : [...seq.defaultBindings];
    }
    return merged;
  } catch {
    return { ...DEFAULT_BINDINGS };
  }
}

export function saveCustomBindings(bindings: CustomBindings): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bindings));
  } catch {
    // localStorage full or unavailable
  }
}

export function getSequenceById(id: string): HotkeySequence | undefined {
  return HOTKEY_SEQUENCES.find((s) => s.id === id);
}

export function sequencesByKind(kind: ReaderKind): HotkeySequence[] {
  return HOTKEY_SEQUENCES.filter((s) => s.kind === kind);
}
